import React, { useState, useCallback } from 'react';
import styles from '../ReportCard.module.scss';
import moment from 'moment';
import Pagination from './Pagination';
import SearchBar from './SearchBar';
import MiniMap from '../MiniMap';
import { getDriverDocuments } from 'service/DriverService';

const CompletedLoadsTableView = ({
  completedLoads,
  completedCount,
  completedNext,
  completedPrev,
  completedPage,
  setCompletedPage,
  completedSearch,
  setCompletedSearch,
  user,
}) => {
  const [visibleMaps, setVisibleMaps] = useState({});
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailMaps, setDetailMaps] = useState({});
  // driverDocs: { [loadId]: { loading, error, docs } }
  const [driverDocs, setDriverDocs] = useState({});
  const [docPreview, setDocPreview] = useState(null); // { url, label }

  const toggleMap = (id, geoType) => {
    const key = `${id}-${geoType}`;
    setVisibleMaps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDetailMap = (key) => {
    setDetailMaps(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchDocumentsIfNeeded = useCallback(async (load) => {
    const canSee = user?.is_admin_of || user?.is_report_of;
    if (!canSee) return;
    const driverId = load.driver_id || load.driver;
    if (!driverId) return;
    const loadId = load.id;
    if (driverDocs[loadId]) return; // already fetched
    setDriverDocs(prev => ({ ...prev, [loadId]: { loading: true, error: null, docs: [] } }));
    try {
      const res = await getDriverDocuments(driverId);
      setDriverDocs(prev => ({ ...prev, [loadId]: { loading: false, error: null, docs: res.data } }));
    } catch (e) {
      setDriverDocs(prev => ({ ...prev, [loadId]: { loading: false, error: 'Ошибка загрузки', docs: [] } }));
    }
  }, [user, driverDocs]);

  const handleRowDoubleClick = (load) => {
    const id = load.id;
    if (expandedRowId === id) {
      setExpandedRowId(null);
    } else {
      setExpandedRowId(id);
      setDetailMaps({});
      fetchDocumentsIfNeeded(load);
    }
  };
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    let date = moment(dateString, 'DD.MM.YYYY HH:mm', true);
    if (!date.isValid()) {
      date = moment(dateString);
      if (date.isValid()) {
        return date.format('DD.MM.YYYY HH:mm');
      }
      return dateString;
    }
    return dateString;
  };

  const renderDetailCard = (load) => {
    const baseName = load.top_base_name || load.chpp_name || load.private_point_name || '-';
    const basePhone = load.top_base_data?.phone || '-';
    const canSeeDocs = user?.is_admin_of || user?.is_report_of;
    const docsState = driverDocs[load.id];

    const baseType = load.top_base_name
      ? 'Топливная база'
      : load.chpp_name
      ? 'ТЭЦ'
      : load.private_point_name
      ? 'Соц. объект / МСУ'
      : null;

    const GeoField = ({ geo, mapKey }) => {
      if (!geo) return null;
      return (
        <div>
          <div className={styles.detailGeoRow}>
            <button className={styles.detailGeoBtn} onClick={() => window.open(`https://www.google.com/maps?q=${geo}`, '_blank')}>📍 Открыть на карте</button>
            <button className={styles.detailGeoBtn} onClick={() => toggleDetailMap(mapKey)}>{detailMaps[mapKey] ? '✕ Скрыть карту' : '🗺️ Мини-карта'}</button>
          </div>
          {detailMaps[mapKey] && <div className={styles.detailMapWrap}><MiniMap geo={geo} /></div>}
        </div>
      );
    };

    const InfoItem = ({ label, value, wide }) => (
      <div className={styles.detailInfoItem} style={wide ? { gridColumn: '1 / -1' } : {}}>
        <span className={styles.detailInfoLabel}>{label}</span>
        <div className={styles.detailInfoValue}>{value}</div>
      </div>
    );

    const PhoneLink = ({ phone }) => {
      if (!phone || phone === '-') return <span>—</span>;
      return (
        <a href={`tel:${phone}`} className={styles.detailPhoneLink}>📞 {phone}</a>
      );
    };

    return (
      <div className={styles.detailCard}>

        {/* ── Шапка: заголовок + бейджи времени ── */}
        <div className={styles.detailCardTopBar}>
          <div className={styles.detailCardTitle}>
            <span>⛏️</span> Подробности погрузки
          </div>
          <div className={styles.detailDurationBadges}>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeLoading}`}>
              <span>⛏️ Погрузка</span>
              <span>{load.duration_str_loading || '—'}</span>
            </div>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeDelivery}`}>
              <span>🚚 Доставка</span>
              <span>{load.duration_str || '—'}</span>
            </div>
            <div className={`${styles.detailDurationBadge} ${styles.detailDurationBadgeTotal}`}>
              <span>⏱️ Общее</span>
              <span>{load.total_duration_str || '—'}</span>
            </div>
          </div>
        </div>

        <div className={styles.detailBody}>

          {load.notes && (
            <div className={styles.detailNotes}>
              <strong>Примечания:</strong> {load.notes}
            </div>
          )}

          {/* ══ Секция 1: Водитель ══ */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>👤 Информация о водителе</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="ФИО" value={load.employee_name || '-'} />
              <InfoItem label="Телефон" value={<PhoneLink phone={load.employee_phone} />} />
              <InfoItem label="Статус" value={load.driver_status || load.status || '-'} />
              <InfoItem label="Гос. номер" value={load.vehicle_number || '-'} />
              <InfoItem label="Модель" value={load.vehicle_model || '-'} />
            </div>

            {/* Документы водителя — только для admin/report */}
            {canSeeDocs && (() => {
              const DOC_LABELS = [
                'Фото ТС',
                'Паспорт (л)',
                'Паспорт (о)',
                'Права (л)',
                'Права (о)',
                'Тех.пасп. (л)',
                'Тех.пасп. (о)',
              ];
              const byDesc = {};
              if (docsState?.docs) {
                docsState.docs.forEach(d => {
                  if (d.description) byDesc[d.description] = d;
                });
              }
              // docs not matching any label
              const otherDocs = docsState?.docs?.filter(d => !DOC_LABELS.includes(d.description)) || [];
              return (
                <div className={styles.detailDocsWrap}>
                  <div className={styles.detailDocsTitle}>📎 Документы водителя</div>
                  {!docsState || docsState.loading ? (
                    <div className={styles.detailDocsLoading}>Загрузка документов…</div>
                  ) : docsState.error ? (
                    <div className={styles.detailDocsError}>{docsState.error}</div>
                  ) : (
                    <>
                      <div className={styles.detailDocsGrid}>
                        {DOC_LABELS.map(label => {
                          const doc = byDesc[label];
                          const fileUrl = doc ? (doc.file_url || doc.file) : null;
                          const isActive = docPreview?.url === fileUrl && fileUrl;
                          return (
                            <button
                              key={label}
                              className={`${styles.detailDocBtn} ${doc ? styles.detailDocBtnHas : styles.detailDocBtnMissing} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => {
                                if (!fileUrl) return;
                                setDocPreview(isActive ? null : { url: fileUrl, label });
                              }}
                              title={doc ? `Открыть: ${label}` : `${label} — не загружено`}
                            >
                              <span className={styles.detailDocBtnIcon}>{doc ? '' : '—'}</span>
                              <span className={styles.detailDocBtnLabel}>{label}</span>
                              {/* {doc && <span className={styles.detailDocBtnArrow}>{isActive ? '▲' : '▼'}</span>} */}
                            </button>
                          );
                        })}
                        {otherDocs.map(doc => {
                          const fileUrl = doc.file_url || doc.file;
                          const isActive = docPreview?.url === fileUrl;
                          return (
                            <button
                              key={doc.id}
                              className={`${styles.detailDocBtn} ${styles.detailDocBtnHas} ${isActive ? styles.detailDocBtnActive : ''}`}
                              onClick={() => setDocPreview(isActive ? null : { url: fileUrl, label: doc.file_name || 'Файл' })}
                            >
                              <span className={styles.detailDocBtnIcon}>📁</span>
                              <span className={styles.detailDocBtnLabel}>{doc.description || doc.file_name || 'Файл'}</span>
                              {/* <span className={styles.detailDocBtnArrow}>{isActive ? '▲' : '▼'}</span> */}
                            </button>
                          );
                        })}
                        {!docsState?.docs?.length && (
                          <div className={styles.detailDocsEmpty} style={{ gridColumn: '1 / -1' }}>Документы не загружены</div>
                        )}
                      </div>
                      {docPreview && (
                        <div className={styles.detailDocPreview}>
                          <div className={styles.detailDocPreviewHeader}>
                            <div className={styles.detailDocPreviewTitle}>
                              <span className={styles.detailDocPreviewIcon}>📄</span>
                              <span>{docPreview.label}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>↗️ Открыть</a>
                              <button className={styles.detailDocPreviewClose} onClick={() => setDocPreview(null)}>✕</button>
                            </div>
                          </div>
                          <div className={styles.detailDocPreviewBody}>
                            <img src={docPreview.url} alt={docPreview.label} className={styles.detailDocPreviewImg}
                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                            />
                            <div style={{ display: 'none', padding: '24px', textAlign: 'center' }}>
                              <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className={styles.detailDocPreviewOpen}>⇩ Скачать файл</a>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* ══ Секция 2: База назначения ══ */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>🏭 Информация о базе назначения</div>
            <div className={styles.detailInfoGrid}>
              <InfoItem label="Название" value={<span style={{ color: '#0d6efd', fontWeight: 700 }}>{baseName}</span>} />
              <InfoItem label="Телефон" value={<PhoneLink phone={basePhone} />} />
              {baseType && <InfoItem label="Тип" value={baseType} />}
            </div>
            {Array.isArray(load.geo_base) && load.geo_base.length > 0 && (
              <div className={styles.detailSubSection}>
                <div className={styles.detailSubSectionTitle}>📍 Точки базы</div>
                {load.geo_base.map((g, i) => (
                  <div key={g.id ?? i} className={styles.detailBranchRow}>
                    <span className={styles.detailBranchName}>{g.name}</span>
                    {g.address && <span className={styles.detailBranchAddr}>{g.address}</span>}
                    {g.geo && (
                      <button className={styles.detailGeoBtn} onClick={() => window.open(`https://www.google.com/maps?q=${g.geo}`, '_blank')}>📍 Открыть</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ══ Секция 3: О погрузке ══ */}
          <div className={styles.detailSection}>
            <div className={styles.detailSectionTitle}>⛏️ О погрузке</div>

            {/* Въезд */}
            <div className={styles.detailSubSection}>
              <div className={styles.detailSubSectionTitle}>🚛 Въезд в Кара-Кече</div>
              <div className={styles.detailInfoGrid}>
                <InfoItem label="Время въезда" value={formatDateTime(load.time_a)} />
                <InfoItem label="Время отметки" value={formatDateTime(load.note_a_datetime)} />
                <InfoItem label="СБ въезда" value={load.guard_name || '-'} />
                <InfoItem label="Телефон СБ" value={<PhoneLink phone={load.guard_phone} />} />
              </div>
              {load.geo_a && (
                <div className={styles.detailGeoSection}>
                  <span className={styles.detailGeoLabel}>Геолокация въезда</span>
                  <GeoField geo={load.geo_a} mapKey={`detail-${load.id}-geo_a`} />
                </div>
              )}
              {load.photo_a && (
                <div className={styles.detailPhotoConfirm}>
                  <div className={styles.detailPhotoConfirmLabel}>📷 ПОДТВЕРЖДЕНИЕ ВЪЕЗДА</div>
                  <a href={load.photo_a} target="_blank" rel="noopener noreferrer">
                    <img src={load.photo_a} alt="Подтверждение въезда" className={styles.detailPhoto} />
                  </a>
                </div>
              )}
            </div>

            {/* Выезд */}
            <div className={styles.detailSubSection}>
              <div className={styles.detailSubSectionTitle}>🏁 Выезд с Кара-Кече</div>
              <div className={styles.detailInfoGrid}>
                <InfoItem label="Время выезда" value={formatDateTime(load.time_a_exit)} />
                <InfoItem label="Время отметки" value={formatDateTime(load.note_a_exit_datetime)} />
                <InfoItem label="СБ выезда" value={load.exit_guard_name || '-'} />
                <InfoItem label="Телефон СБ" value={<PhoneLink phone={load.exit_guard_phone} />} />
              </div>
              {load.geo_a_exit && (
                <div className={styles.detailGeoSection}>
                  <span className={styles.detailGeoLabel}>Геолокация выезда</span>
                  <GeoField geo={load.geo_a_exit} mapKey={`detail-${load.id}-geo_a_exit`} />
                </div>
              )}
            </div>

            {/* Прибытие на базу */}
            <div className={styles.detailSubSection}>
              <div className={styles.detailSubSectionTitle}>🏭 Прибытие на базу</div>
              <div className={styles.detailInfoGrid}>
                <InfoItem label="Время прибытия" value={formatDateTime(load.time_b)} />
                <InfoItem label="Время отметки" value={formatDateTime(load.note_b_datetime)} />
                <InfoItem label="Оператор базы" value={load.by_guard_name || '-'} />
                <InfoItem label="Телефон оператора" value={<PhoneLink phone={load.by_guard_phone} />} />
              </div>
              {load.geo_b && (
                <div className={styles.detailGeoSection}>
                  <span className={styles.detailGeoLabel}>Геолокация базы</span>
                  <GeoField geo={load.geo_b} mapKey={`detail-${load.id}-geo_b`} />
                </div>
              )}
              {load.photo_b && (
                <div className={styles.detailPhotoConfirm}>
                  <div className={styles.detailPhotoConfirmLabel}>📷 ПОДТВЕРЖДЕНИЕ ПОГРУЗКИ</div>
                  <a href={load.photo_b} target="_blank" rel="noopener noreferrer">
                    <img src={load.photo_b} alt="Подтверждение погрузки" className={styles.detailPhoto} />
                  </a>
                </div>
              )}
            </div>

          </div>

        </div>
      </div>
    );
  };

  return (
    <div className={styles.table_info_wrapper}>
      <Pagination
        page={completedPage}
        setPage={setCompletedPage}
        count={completedCount}
        hasNext={!!completedNext}
        hasPrev={!!completedPrev}
        shownCount={completedLoads.length}
      />

      <div className={styles.completedSearchRow}>
        <SearchBar
          value={completedSearch}
          onChange={setCompletedSearch}
          onClear={() => setCompletedSearch('')}
          placeholder="Поиск по водителю, машине, базе..."
        />
        <span className={styles.completedBadge}>✅ Завершённые поездки</span>
      </div>

      <div className={styles.tableScrollWrap}>
        <table className={`${styles.dataTable} ${styles.completedTable}`}>
          <thead>
            <tr>
              <th>#</th>
              <th>Машина</th>
              <th>Водитель</th>
              <th>База назначения</th>
              <th>Въезд (Кара-Кече)</th>
              <th>Выезд (Кара-Кече)</th>
              <th>Прибытие (База)</th>
              <th>Погрузка</th>
              <th>Доставка</th>
              <th>Общее</th>
              <th>Примечания</th>
            </tr>
          </thead>
          <tbody>
            {completedLoads && completedLoads.length > 0 ? (
              completedLoads.map((load, idx) => {
                const driverName = load.employee_name || '-';
                const vehicleNumber = load.vehicle_number || '-';
                const baseName = load.top_base_name || load.chpp_name || load.private_point_name || '-';
                const rowNumber = (completedPage - 1) * 10 + idx + 1;
                const statusText = load.status === 'Въехал в точку Б' ? 'Доставил в Базу' : load.status || '-';

                const mapKeyA = `${load.id}-geo_a`;
                const mapKeyAExit = `${load.id}-geo_a_exit`;
                const mapKeyB = `${load.id}-geo_b`;

                return (
                  <React.Fragment key={load.id}>
                    <tr
                      onDoubleClick={() => handleRowDoubleClick(load)}
                      onClick={() => { if (window.innerWidth <= 640) handleRowDoubleClick(load); }}
                      style={{ cursor: 'pointer', userSelect: 'none', background: expandedRowId === load.id ? '#eef4ff' : undefined }}
                      title="Двойной клик — подробная карточка"
                    >
                      <td>{rowNumber}</td>
                      <td data-label="Машина">
                        <div style={{ fontWeight: '700' }}>{vehicleNumber}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{load.vehicle_model || '-'}</div>
                      </td>
                      <td data-label="Водитель">
                        <div>👤 {driverName}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{load.employee_phone || '-'}</div>
                      </td>
                      <td data-label="База" style={{ color: '#0d6efd', fontWeight: '600' }}>{baseName}</td>
                      <td data-label="Въезд КК">
                        <div>{formatDateTime(load.time_a)}</div>
                        {load.geo_a && (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                            <button onClick={() => window.open(`https://www.google.com/maps?q=${load.geo_a}`, '_blank')} className={styles.geoLinkBtn}>📍</button>
                            <button onClick={() => toggleMap(load.id, 'geo_a')} className={styles.mapToggleBtn}>{visibleMaps[mapKeyA] ? '✕' : '🗺️'}</button>
                          </div>
                        )}
                      </td>
                      <td data-label="Выезд КК">
                        <div>{formatDateTime(load.time_a_exit)}</div>
                        {load.geo_a_exit && (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                            <button onClick={() => window.open(`https://www.google.com/maps?q=${load.geo_a_exit}`, '_blank')} className={styles.geoLinkBtn}>📍</button>
                            <button onClick={() => toggleMap(load.id, 'geo_a_exit')} className={styles.mapToggleBtn}>{visibleMaps[mapKeyAExit] ? '✕' : '🗺️'}</button>
                          </div>
                        )}
                      </td>
                      <td data-label="Прибытие">
                        <div>{formatDateTime(load.time_b)}</div>
                        {load.geo_b && (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginTop: '4px' }}>
                            <button onClick={() => window.open(`https://www.google.com/maps?q=${load.geo_b}`, '_blank')} className={styles.geoLinkBtn}>📍</button>
                            <button onClick={() => toggleMap(load.id, 'geo_b')} className={styles.mapToggleBtn}>{visibleMaps[mapKeyB] ? '✕' : '🗺️'}</button>
                          </div>
                        )}
                      </td>
                      <td data-label="Погрузка">{load.duration_str_loading || '-'}</td>
                      <td data-label="Доставка" style={{ color: '#198754' }}>{load.duration_str || '-'}</td>
                      <td data-label="Общее">{load.total_duration_str || '-'}</td>
                      <td data-label="Примечания" style={{ fontSize: '12px' }}>
                        {(user?.is_admin_of || user?.is_report_of) && load.photo_b && (
                          <div style={{ marginBottom: '6px', textAlign: 'center' }}>
                            <a href={load.photo_b} target="_blank" rel="noopener noreferrer">
                              <img
                                src={load.photo_b}
                                alt="Фото"
                                style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #0d6efd' }}
                              />
                            </a>
                          </div>
                        )}
                        {load.notes || '-'}
                      </td>
                    </tr>
                    {load.geo_a && visibleMaps[mapKeyA] && (
                      <tr>
                        <td colSpan={11} style={{ padding: 0 }}>
                          <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                            <MiniMap geo={load.geo_a} />
                          </div>
                        </td>
                      </tr>
                    )}
                    {load.geo_a_exit && visibleMaps[mapKeyAExit] && (
                      <tr>
                        <td colSpan={11} style={{ padding: 0 }}>
                          <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                            <MiniMap geo={load.geo_a_exit} />
                          </div>
                        </td>
                      </tr>
                    )}
                    {load.geo_b && visibleMaps[mapKeyB] && (
                      <tr>
                        <td colSpan={11} style={{ padding: 0 }}>
                          <div style={{ height: '200px', borderRadius: '8px', overflow: 'hidden' }}>
                            <MiniMap geo={load.geo_b} />
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedRowId === load.id && (
                      <tr className={styles.detailCardRow}>
                        <td colSpan={11}>
                          {renderDetailCard(load)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={11} className={styles.noData}>Нет данных</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={completedPage}
        setPage={setCompletedPage}
        count={completedCount}
        hasNext={!!completedNext}
        hasPrev={!!completedPrev}
        shownCount={completedLoads.length}
        style={{ marginTop: '8px', marginBottom: 0 }}
      />
    </div>
  );
};

export default CompletedLoadsTableView;